// ═══════════════════════════════════════════════════════════════════════════════
// Subscription promotions — new-user first month (monthly only) + multi limited-time
// ═══════════════════════════════════════════════════════════════════════════════
export const SUBSCRIPTION_PROMOTIONS_SETTING_KEY = 'subscription_promotions';
export const SUBSCRIPTION_BILLING_INTERVAL = {
    MONTH: 'month',
    YEAR: 'year',
};
export const SUBSCRIPTION_PROMO_TYPE = {
    NEW_USER_FIRST_MONTH: 'new_user_first_month',
    LIMITED_TIME: 'limited_time',
};
export const DEFAULT_SUBSCRIPTION_PROMOTIONS = {
    newUserFirstMonth: {
        enabled: false,
        amountOff: 0,
        stripeCouponId: '',
    },
    limitedTimeOffers: [],
};
/** Stripe zero-decimal currencies (amount equals major units). */
const STRIPE_ZERO_DECIMAL_CURRENCIES = new Set([
    'bif',
    'clp',
    'djf',
    'gnf',
    'jpy',
    'kmf',
    'krw',
    'mga',
    'pyg',
    'rwf',
    'ugx',
    'vnd',
    'vuv',
    'xaf',
    'xof',
    'xpf',
]);
/** Multiplier from Admin major units → Stripe minor units for `currency`. */
export const stripeMinorUnitMultiplier = (currency) => {
    const code = currency.trim().toLowerCase();
    if (code === '') {
        return 100;
    }
    return STRIPE_ZERO_DECIMAL_CURRENCIES.has(code) ? 1 : 100;
};
/** Convert Admin major-unit amountOff to Stripe minor units. */
export const majorAmountOffToStripeMinor = (majorAmount, currency) => {
    const major = Number.isFinite(majorAmount) ? Math.floor(majorAmount) : 0;
    if (major <= 0) {
        return 0;
    }
    return major * stripeMinorUnitMultiplier(currency);
};
const DATETIME_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
export const isSubscriptionPromoDatetime = (value) => DATETIME_RE.test(value.trim());
export const isSubscriptionBillingInterval = (value) => value === SUBSCRIPTION_BILLING_INTERVAL.MONTH || value === SUBSCRIPTION_BILLING_INTERVAL.YEAR;
const parsePromoDatetimeToUtcMs = (value) => {
    const trimmed = value.trim();
    if (!DATETIME_RE.test(trimmed)) {
        return null;
    }
    const [datePart, timePart] = trimmed.split(' ');
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = timePart.split(':').map(Number);
    return Date.UTC(y, m - 1, d, hh, mm, 0, 0);
};
/** True when `at` is within [startsAt, endsAt] inclusive (UTC minutes). */
export const isWithinLimitedTimePromoWindow = (at, startsAt, endsAt) => {
    const startMs = parsePromoDatetimeToUtcMs(startsAt);
    const endMs = parsePromoDatetimeToUtcMs(endsAt);
    if (startMs == null || endMs == null || endMs < startMs) {
        return false;
    }
    const t = at.getTime();
    return t >= startMs && t <= endMs;
};
const clampAmount = (n) => {
    if (!Number.isFinite(n) || n < 0) {
        return 0;
    }
    return Math.floor(n);
};
const createOfferId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `offer_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};
export const pickMaxSubscriptionPromo = (candidates) => {
    if (candidates.length === 0) {
        return null;
    }
    return candidates.reduce((best, row) => {
        if (row.amountOff > best.amountOff) {
            return row;
        }
        if (row.amountOff < best.amountOff) {
            return best;
        }
        // Tie-break: earlier endsAt, then promoOfferId / stripeCouponId
        const bestEnds = best.endsAt ?? '9999-12-31 23:59';
        const rowEnds = row.endsAt ?? '9999-12-31 23:59';
        if (rowEnds < bestEnds) {
            return row;
        }
        if (rowEnds > bestEnds) {
            return best;
        }
        const bestKey = best.promoOfferId ?? best.stripeCouponId;
        const rowKey = row.promoOfferId ?? row.stripeCouponId;
        return rowKey < bestKey ? row : best;
    });
};
/** Collect eligible limited-time offers for an interval at `at` (minor units). */
export const collectLimitedTimePromoCandidates = (input) => {
    const candidates = [];
    for (const offer of input.offers) {
        if (!offer.enabled) {
            continue;
        }
        if (offer.billingInterval !== input.billingInterval) {
            continue;
        }
        if (offer.amountOff <= 0 || offer.stripeCouponId.trim() === '') {
            continue;
        }
        if (!isWithinLimitedTimePromoWindow(input.at, offer.startsAt, offer.endsAt)) {
            continue;
        }
        candidates.push({
            promoType: SUBSCRIPTION_PROMO_TYPE.LIMITED_TIME,
            amountOff: majorAmountOffToStripeMinor(offer.amountOff, input.currency),
            stripeCouponId: offer.stripeCouponId.trim(),
            endsAt: offer.endsAt.trim(),
            promoOfferId: offer.id,
        });
    }
    return candidates;
};
/**
 * Eligible promos → take the single largest amountOff (no stacking).
 * newUserFirstMonth: monthly first paid invoice only.
 * limitedTimeOffers: any invoice whose `at` falls in an offer window for that interval.
 */
export const resolveSubscriptionPromoForCharge = (input) => {
    const candidates = [];
    const { setting, at, isFirstPaidSubscriptionInvoice, listPriceAmount, currency, billingInterval } = input;
    if (billingInterval === SUBSCRIPTION_BILLING_INTERVAL.MONTH &&
        isFirstPaidSubscriptionInvoice) {
        const newUser = setting.newUserFirstMonth;
        if (newUser.enabled && newUser.amountOff > 0 && newUser.stripeCouponId.trim() !== '') {
            candidates.push({
                promoType: SUBSCRIPTION_PROMO_TYPE.NEW_USER_FIRST_MONTH,
                amountOff: majorAmountOffToStripeMinor(newUser.amountOff, currency),
                stripeCouponId: newUser.stripeCouponId.trim(),
                endsAt: null,
                promoOfferId: null,
            });
        }
    }
    candidates.push(...collectLimitedTimePromoCandidates({
        offers: setting.limitedTimeOffers,
        billingInterval,
        at,
        currency,
    }));
    const picked = pickMaxSubscriptionPromo(candidates);
    if (!picked || picked.amountOff <= 0) {
        return null;
    }
    return {
        ...picked,
        amountDueAfterDiscount: Math.max(0, listPriceAmount - picked.amountOff),
    };
};
/**
 * Compare live promo vs locked promo; prefer higher amountOff.
 * If live is absent or worse, keep locked.
 */
export const pickLiveOrLockedPromo = (input) => {
    const { live, locked, listPriceAmount, billingInterval } = input;
    const lockedMatches = locked &&
        locked.billingInterval === billingInterval &&
        locked.amountOff > 0 &&
        locked.stripeCouponId.trim() !== '';
    if (live && lockedMatches && live.amountOff > locked.amountOff) {
        return live;
    }
    if (lockedMatches) {
        return {
            promoType: SUBSCRIPTION_PROMO_TYPE.LIMITED_TIME,
            amountOff: locked.amountOff,
            stripeCouponId: locked.stripeCouponId.trim(),
            endsAt: locked.endsAt,
            promoOfferId: locked.promoOfferId,
            amountDueAfterDiscount: Math.max(0, listPriceAmount - locked.amountOff),
        };
    }
    return live;
};
const normalizeOffer = (raw, index) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }
    const row = raw;
    const billingInterval = isSubscriptionBillingInterval(row.billingInterval)
        ? row.billingInterval
        : SUBSCRIPTION_BILLING_INTERVAL.MONTH;
    const startsAt = typeof row.startsAt === 'string' && isSubscriptionPromoDatetime(row.startsAt.trim())
        ? row.startsAt.trim()
        : '2026-01-01 00:00';
    const endsAt = typeof row.endsAt === 'string' && isSubscriptionPromoDatetime(row.endsAt.trim())
        ? row.endsAt.trim()
        : '2026-01-01 00:00';
    const id = typeof row.id === 'string' && row.id.trim() !== '' ? row.id.trim() : `offer_${index}_${createOfferId()}`;
    const adminNote = typeof row.adminNote === 'string' && row.adminNote.trim() !== ''
        ? row.adminNote.trim().slice(0, 200)
        : undefined;
    return {
        id,
        enabled: row.enabled === true,
        billingInterval,
        amountOff: clampAmount(Number(row.amountOff ?? 0)),
        stripeCouponId: typeof row.stripeCouponId === 'string' ? row.stripeCouponId.trim() : '',
        startsAt,
        endsAt,
        ...(adminNote ? { adminNote } : {}),
    };
};
/** Migrate legacy `{ limitedTime }` into `limitedTimeOffers` array. */
export const migrateLegacyLimitedTimeToOffers = (raw) => {
    if (Array.isArray(raw.limitedTimeOffers)) {
        return raw.limitedTimeOffers
            .map((row, index) => normalizeOffer(row, index))
            .filter((row) => row != null);
    }
    const limited = raw.limitedTime;
    if (!limited || typeof limited !== 'object' || Array.isArray(limited)) {
        return [];
    }
    const offer = normalizeOffer({
        ...limited,
        id: 'legacy_limited_time',
        billingInterval: SUBSCRIPTION_BILLING_INTERVAL.MONTH,
    }, 0);
    return offer ? [offer] : [];
};
export const normalizeSubscriptionPromotionsSetting = (input) => {
    const row = input;
    const newUserRaw = row.newUserFirstMonth && typeof row.newUserFirstMonth === 'object'
        ? row.newUserFirstMonth
        : {};
    const offers = migrateLegacyLimitedTimeToOffers(row);
    return {
        newUserFirstMonth: {
            enabled: newUserRaw.enabled === true,
            amountOff: clampAmount(Number(newUserRaw.amountOff ?? 0)),
            stripeCouponId: typeof newUserRaw.stripeCouponId === 'string' ? newUserRaw.stripeCouponId.trim() : '',
        },
        limitedTimeOffers: offers,
    };
};
export const lockedPromoFromResolved = (input) => {
    const year = input.lockedAt.getUTCFullYear();
    const month = String(input.lockedAt.getUTCMonth() + 1).padStart(2, '0');
    const day = String(input.lockedAt.getUTCDate()).padStart(2, '0');
    const hours = String(input.lockedAt.getUTCHours()).padStart(2, '0');
    const minutes = String(input.lockedAt.getUTCMinutes()).padStart(2, '0');
    return {
        billingInterval: input.billingInterval,
        amountOff: input.promo.amountOff,
        stripeCouponId: input.promo.stripeCouponId,
        promoOfferId: input.promo.promoOfferId,
        endsAt: input.promo.endsAt,
        lockedAt: `${year}-${month}-${day} ${hours}:${minutes}`,
    };
};
//# sourceMappingURL=subscriptionPromotions.js.map